import { z } from 'zod';
import { contractStatusSchema } from './contracts.js';
export const fileUploadContext = z.object({
    source: z.literal('file'),
});
export const ownershipUploadContext = z.object({
    source: z.literal('ownership'),
    ownershipId: z.number(),
});
export const portfolioUploadContext = z.object({
    source: z.literal('portfolio'),
    portfolioId: z.number(),
    ownershipId: z.number().optional(),
});
export const propertyUploadContext = z.object({
    source: z.literal('property'),
    propertyId: z.number(),
    portfolioId: z.number().optional(),
});
export const contractUploadContext = z.object({
    source: z.literal('contract'),
    contractStatus: contractStatusSchema,
    contractId: z.number(),
    contractStatusId: z.number(),
    ownershipId: z.number().optional(),
    portfolioId: z.number().optional(),
    propertyId: z.number().optional(),
});
export const invoiceUploadContext = z.object({
    source: z.literal('invoice'),
    invoiceId: z.number(),
    ownershipId: z.number().optional(),
    portfolioId: z.number().optional(),
    propertyId: z.number().optional(),
});
export const rfpProposalUploadContext = z.object({
    source: z.literal('rfp-proposal'),
    rfpId: z.number(),
    ownershipId: z.number(),
    portfolioId: z.number().optional(),
    propertyId: z.number().optional(),
});
export const documentUploadContext = z.discriminatedUnion('source', [
    fileUploadContext,
    ownershipUploadContext,
    portfolioUploadContext,
    propertyUploadContext,
    contractUploadContext,
    invoiceUploadContext,
    rfpProposalUploadContext,
]);
export const documentCategorySchema = z
    .enum([
    'Financials',
    'Vendor Contracts',
    'Maintenance',
    'Compliance',
    'Legal & Governance',
    'Resident & Tenant Relations',
    'Insurance',
    'Board Room',
])
    .describe('The category a document is filed under. Only associations have `Board Room`.');
export const LEGACY_DOCUMENT_SUBCATEGORY_ALIASES = {
    'Budget & Financial Reports': {
        readAs: ['Budget', 'Financial Reports'],
        writeAs: 'Financial Reports',
    },
    'Budget and Financial Reports': {
        readAs: ['Budget', 'Financial Reports'],
        writeAs: 'Financial Reports',
    },
};
export function normalizeDocumentSubcategory(subcategory) {
    return LEGACY_DOCUMENT_SUBCATEGORY_ALIASES[subcategory]?.writeAs ?? subcategory;
}
export function documentSubcategoryFilterValues(subcategory) {
    const splitLabels = LEGACY_DOCUMENT_SUBCATEGORY_ALIASES[subcategory]?.readAs ?? [];
    const legacyLabels = Object.entries(LEGACY_DOCUMENT_SUBCATEGORY_ALIASES)
        .filter(([, alias]) => alias.readAs.includes(subcategory))
        .map(([legacyLabel]) => legacyLabel);
    return [...new Set([subcategory, ...splitLabels, ...legacyLabels])];
}
export function createOrderedList(entries) {
    return {
        keys: entries.map(([k]) => k),
        values: entries.map(([, v]) => v),
    };
}
export function* orderedListEntries(list) {
    const limit = Math.min(list.keys.length, list.values.length);
    for (let i = 0; i < limit; i++) {
        yield [list.keys[i], list.values[i]];
    }
}
const SUBCATEGORY_CATALOG = {
    Financials: createOrderedList([
        [
            'Budget',
            'Annual budgets, operating budgets, reserve budgets, budget forecasts and budget planning documents',
        ],
        [
            'Financial Reports',
            'Quarterly financial statements, profit and loss reports, balance sheets, financial forecasts and budget variance reports',
        ],
        [
            'Invoices & Receipts',
            'Vendor invoices, payment receipts, billing statements, expense documentation and proof of payment records',
        ],
        [
            'Rent or Association Payment Records',
            'Monthly rent payments, HOA dues, assessment payments, payment ledgers and tenant payment histories',
        ],
        [
            'Special Assessments',
            'One-time or special assessment notices, capital improvement assessments, emergency repair assessments and collection records',
        ],
        [
            'Reserve Study',
            'Reserve fund analyses, capital planning studies, component life expectancy reports and funding recommendations',
        ],
        ['Other', 'Other financial documents not covered by the above categories'],
    ]),
    'Vendor Contracts': createOrderedList([
        [
            'Property Management',
            'Contracts for property management services, HOA management, community administration and oversight',
        ],
        [
            'Insurance',
            'Insurance policies including general liability, property insurance, D&O, workers compensation',
        ],
        [
            'Legal Agreements',
            'Legal service contracts, attorney retainers, legal consultation agreements',
        ],
        [
            'Architecture',
            'Architectural design services, building plans, renovation designs and construction drawings',
        ],
        [
            'Engineering',
            'Engineering services including structural, mechanical, civil, electrical engineering assessments',
        ],
        [
            'Electrician',
            'Electrical service contracts, wiring, lighting, electrical system maintenance and emergency repairs',
        ],
        [
            'Plumbing',
            'Plumbing service contracts, pipe repairs, fixture installation, water system maintenance',
        ],
        [
            'Roofing',
            'Roofing contracts for installation, repair, maintenance, inspection and warranty services',
        ],
        [
            'Siding',
            'Siding installation, repair, maintenance contracts and exterior building envelope services',
        ],
        [
            'Maintenance',
            'General maintenance services, routine upkeep, preventive maintenance agreements and facility services',
        ],
        [
            'Gate/Doorman',
            'Security gate services, doorman services, concierge, access control staffing',
        ],
        [
            'Landscaping',
            'Landscaping services, grounds maintenance, lawn care, tree services, irrigation and snow removal',
        ],
        [
            'Pool',
            'Pool maintenance, pool cleaning, chemical treatment, pool repair and equipment services',
        ],
        [
            'Parking',
            'Parking lot maintenance, striping, resurfacing, snow removal and parking management',
        ],
        [
            'Security',
            'Security services, alarm systems, surveillance cameras, guard services and access control systems',
        ],
        [
            'Pest Control',
            'Pest control services, extermination, rodent control, termite treatment and ongoing pest management',
        ],
        ['Other', 'Other vendor services not covered by the above categories'],
    ]),
    Maintenance: createOrderedList([
        [
            'Maintenance Logs',
            'Routine maintenance schedules, preventive maintenance records, completed work logs and maintenance action plans',
        ],
        [
            'Work Orders & Service Requests',
            'Repair work orders, service requests from residents, maintenance tickets and completion documentation',
        ],
        [
            'Capital Improvements',
            'Major renovation projects including roof replacement, facade upgrades, HVAC replacements, structural improvements and building modernization',
        ],
        ['Other', 'Other maintenance and compliance documents not covered by the above categories'],
    ]),
    Compliance: createOrderedList([
        [
            'Inspection Reports',
            'Building inspections including siding, fire safety, elevator, structural, electrical and annual compliance inspections',
        ],
        [
            'Violation & Remediation Records',
            'Code violations, compliance notices, remediation plans, correction documentation and follow-up reports',
        ],
        [
            'Compliance Updates',
            'Regulatory compliance notifications, code changes, safety updates and requirement modifications',
        ],
    ]),
    'Resident & Tenant Relations': createOrderedList([
        [
            'Lease/Rental Agreements',
            'Residential lease agreements, rental contracts, lease renewals, amendments and tenant occupancy agreements',
        ],
        [
            'Move-in/Move-out Checklists',
            'Property condition reports, move-in inspections, move-out inspections, damage assessments and security deposit documentation',
        ],
        [
            'Communication Records',
            'Resident correspondence, newsletters, notices, community announcements, complaint responses and general communications',
        ],
        ['Other', 'Other resident and tenant relation documents not covered by the above categories'],
    ]),
    'Legal & Governance': createOrderedList([
        [
            'Governing Documents',
            'Bylaws, Declaration of Covenants, Articles of Incorporation, Certificate of Formation, Operating Agreements, amendments and foundational legal documents',
        ],
        [
            'Condominium Map',
            'Official condominium site plans, unit boundary maps, common area designations and property plats',
        ],
        [
            'Property Card',
            'Official property records, assessor cards, property tax cards and municipal property documentation',
        ],
        [
            'Legal Matters',
            'Legal opinions, lawsuits, legal claims, summons, court documents, settlement agreements and attorney correspondence',
        ],
        [
            'Permits & Licenses',
            'Building permits, construction permits, elevator certifications, pool permits, occupancy permits and business licenses',
        ],
        ['Other', 'Other legal and governance documents not covered by the above categories'],
    ]),
    Insurance: createOrderedList([
        [
            'Insurance Policies',
            'General liability insurance, property insurance, Directors & Officers insurance, workers compensation, umbrella policies and coverage documents',
        ],
        [
            'Insurance Claims',
            'Filed insurance claims, claim documentation, damage reports, claim settlements and loss recovery records',
        ],
        ['Other', 'Other insurance documents not covered by the above categories'],
    ]),
    'Board Room': createOrderedList([
        [
            'Election',
            'Board election materials, candidate nominations, voting ballots, election results and annual meeting election records',
        ],
        [
            'Board of Directors',
            'Board member rosters, director appointments, resignations, officer positions and board composition records',
        ],
        [
            'Board Meeting Records',
            'Meeting agendas, board meeting minutes, resolutions, voting records, executive session notes and action items',
        ],
        [
            'Annual Meeting',
            'Annual meeting agendas, notices, minutes, election materials and annual meeting records',
        ],
        ['Other', 'Other board-related documents not covered by the above categories'],
    ]),
};
export const DOCUMENT_SUBCATEGORIES = SUBCATEGORY_CATALOG;
export const documentCategorizationOutputSchema = z.object({
    classification: z.string().describe('The chosen category from the provided options'),
    reason: z.string().describe('Clear explanation of why this classification was chosen'),
});
export const describedDocumentInput = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('content'),
        content: z.string(),
        mimeType: z.string(),
        name: z.string().optional(),
        documentId: z.number().optional(),
        documentIdType: z.string().optional(),
    }),
    z.object({
        type: z.literal('url'),
        url: z.string(),
        mimeType: z.string().optional(),
        documentId: z.number().optional(),
        documentIdType: z.string().optional(),
    }),
]);
export const DOCUMENT_SUMMARY_CLAIM_LEASE_MS = 15 * 60_000;
export const DOCUMENT_SUMMARY_RETRY_DELAY_MS = 16 * 60_000;
export const DOCUMENT_SUMMARY_GENERATION_ATTEMPTS = 4;
export const DOCUMENT_SUMMARY_CLEANUP_ATTEMPTS = 2;
export const DOCUMENT_SUMMARY_RETRY_LIMIT = DOCUMENT_SUMMARY_GENERATION_ATTEMPTS + DOCUMENT_SUMMARY_CLEANUP_ATTEMPTS - 1;
export const DOCUMENT_SUMMARY_RETRY_HORIZON_MS = DOCUMENT_SUMMARY_RETRY_LIMIT *
    (DOCUMENT_SUMMARY_CLAIM_LEASE_MS + DOCUMENT_SUMMARY_RETRY_DELAY_MS);
//# sourceMappingURL=documents.js.map